import { Fragment } from 'react';
import { Path } from '@react-pdf/renderer';
import { values } from 'lodash';

/**
 * @function Storage
 * @description Renders storage rack layouts in a PDF document for warehouse/room areas.
 * 
 * @param {Object} props
 * @param {boolean} props.inContainer - Whether rendering container-level racks
 * @param {string} props.areaId - Specific area ID to render
 * @param {number} props.scale - Scale factor for dimensions
 * @param {Object} props.content - Area and rack configuration data
 * @param {Object} props.content.areas - Areas data by ID
 * @param {Object} props.constants - Configuration constants
 * @param {Object} props.constants.area.storage - Storage dimensions
 * @param {number} props.constants.area.storage.beam - Beam length
 * @param {number} props.constants.area.storage.depth - Rack depth
 * 
 * @description
 * Generates SVG paths for storage racks by:
 * - Converting area coordinates to PDF space
 * - Applying rack dimensions from constants
 * - Supporting horizontal/vertical orientations
 * - Drawing racks based on xArray/yArray positions
 * 
 * @note
 * - Uses @react-pdf/renderer Path components
 * - Calculates positions in scaled coordinate space
 * - Supports both container and area-specific views
 * - Handles orientation-based dimension swapping
 */
const Storage = (props) => {
  const { inContainer, areaId, scale, content: { areas }, constants: { area: { storage } } } = props;
  const areaArray = (areaId ? [areas?.[areaId]] : values(areas)).map(area => ({
    id: area.id,
    ...area.location,
    racks: area.rackArrays,
    ver: area.storageOrientation === 'horizontal'
  }));

  // add the storage racks
  const { beam, depth } = storage;
  const shortSide = depth * scale; // 48"
  const longSide = beam * scale // 96"

  const racks = areaArray.flatMap(area => {
    const x0 = 2 + (inContainer ? area.left * scale : 0);
    const y0 = 2 + (inContainer ? area.top * scale : 0);
    const xArray = area.racks?.xArray || [];
    const yArray = area.racks?.yArray || [];
    const ver = area.ver;
    return yArray.flatMap((y, i) =>
      xArray.flatMap((x, j) =>
        ({ d: `m${x0 + x * scale} ${y0 + y * scale} h${ver ? longSide : shortSide}v${ver ? shortSide : longSide}h${- (ver ? longSide : shortSide)}z` })
      )
    )
  });

  return (
    <Fragment>
      {racks.map((rack, index) => <Path key={`rack-${index}`} {...rack} stroke="#a5aaad" />)}
    </Fragment>
  )
};

export default Storage;